import React, { useState, useRef } from 'react';
import { Card, Button, Form, Dimmer } from "tabler-react";
import { useFetch } from '../../../lib/useFetch';
import PutObject from '../../../lib/putObject';
import { toBase64 } from '../../../lib/helpers';

const SubcategoryEditModal = ({ recordId, closeMainModal, reload }) => {
    const { data: form, loading, handleChange } = useFetch(`categories/sub-category/${recordId}`);
    const [saveLoading, setSaveLoading] = useState(false);
    const [error, setError] = useState('');
    const [iconPreview, setIconPreview] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [hasNewIcon, setHasNewIcon] = useState(false);
    const [hasNewPhoto, setHasNewPhoto] = useState(false);
    const [removeIcon, setRemoveIcon] = useState(false);
    const [removePhoto, setRemovePhoto] = useState(false);
    const iconFile = useRef(null);
    const photoFile = useRef(null);

    const validateForm = () => {
        if (!form?.name) {
            setError('Please enter subcategory name');
            return false;
        }
        if (!form?.pricePerKg && form?.pricePerKg !== 0) {
            setError('Please enter price per kg');
            return false;
        }
        if (!form?.minTravelerReward && form?.minTravelerReward !== 0) {
            setError('Please enter traveler reward');
            return false;
        }
        if (!form?.weight && form?.weight !== 0) {
            setError('Please enter weight');
            return false;
        }
        if (iconFile.current.files.length > 0 && !iconFile.current.files[0].type.includes('image')) {
            setError('Please select a valid image file for icon');
            return false;
        }
        if (photoFile.current.files.length > 0 && !photoFile.current.files[0].type.includes('image')) {
            setError('Please select a valid image file for photo');
            return false;
        }
        return true;
    };

    const handleIconChange = async (e) => {
        const file = e.target.files[0];
        if (file && file.type.includes('image')) {
            const preview = await toBase64(file);
            setIconPreview(preview);
            setHasNewIcon(true);
            setRemoveIcon(false);
            setError('');
        } else if (file) {
            setError('Please select a valid image file for icon');
            setIconPreview(null);
            setHasNewIcon(false);
        }
    };

    const handlePhotoChange = async (e) => {
        const file = e.target.files[0];
        if (file && file.type.includes('image')) {
            const preview = await toBase64(file);
            setPhotoPreview(preview);
            setHasNewPhoto(true);
            setRemovePhoto(false);
            setError('');
        } else if (file) {
            setError('Please select a valid image file for photo');
            setPhotoPreview(null);
            setHasNewPhoto(false);
        }
    };

    const handleIconDropZoneClick = () => {
        iconFile.current.click();
    };

    const handlePhotoDropZoneClick = () => {
        photoFile.current.click();
    };

    const handleRemoveIcon = (e) => {
        e.stopPropagation();
        setIconPreview(null);
        setHasNewIcon(false);
        setRemoveIcon(true);
        iconFile.current.value = '';
        // Force reset the file input
        if (iconFile.current) {
            iconFile.current.type = '';
            iconFile.current.type = 'file';
        }
    };

    const handleRemovePhoto = (e) => {
        e.stopPropagation();
        setPhotoPreview(null);
        setHasNewPhoto(false);
        setRemovePhoto(true);
        photoFile.current.value = '';
        // Force reset the file input
        if (photoFile.current) {
            photoFile.current.type = '';
            photoFile.current.type = 'file';
        }
    };

    const saveEdit = async () => {
        setError('');
        if (!validateForm()) return;

        try {
            setSaveLoading(true);
            
            const editedCategory = {
                _id: recordId,
                name: form.name,
                pricePerKg: form.pricePerKg,
                minTravelerReward: form.minTravelerReward,
                categoryId: form.categoryId,
                weight: form.weight
            };

            // Handle icon
            if (removeIcon) {
                editedCategory.icon = '';
            } else if (hasNewIcon && iconFile.current.files.length > 0) {
                editedCategory.icon = await toBase64(iconFile.current.files[0]);
            }

            // Handle photo
            if (removePhoto) {
                editedCategory.image = '';
            } else if (hasNewPhoto && photoFile.current.files.length > 0) {
                editedCategory.image = await toBase64(photoFile.current.files[0]);
            }
            
            await PutObject('categories/sub-category', editedCategory);
            
            if (typeof reload === 'function') {
                reload();
            }
            closeMainModal();
        } catch (err) {
            setError('Failed to update subcategory. Please try again.');
        } finally {
            setSaveLoading(false);
        }
    };

    // Get current icon source for preview
    const getCurrentIconSrc = () => {
        if (removeIcon) return null;
        if (hasNewIcon && iconPreview) {
            return iconPreview;
        }
        return form?.icon;
    };

    // Get current photo source for preview
    const getCurrentPhotoSrc = () => {
        if (removePhoto) return null;
        if (hasNewPhoto && photoPreview) {
            return photoPreview;
        }
        return form?.image;
    };

    return (
        <Card>
            <Dimmer active={loading} loader={loading}>
                <Card.Header>
                    <Card.Title>Edit Subcategory</Card.Title>
                    <Card.Options>
                        <Button
                            size="sm"
                            icon="x"
                            onClick={closeMainModal}
                        />
                    </Card.Options>
                </Card.Header>
                {!loading && form && (
                    <>
                        <Card.Body>
                            {error && <div className="alert alert-danger">{error}</div>}
                            <Form onSubmit={(e) => { e.preventDefault(); saveEdit(); }}>
                                <Form.Input
                                    name='name'
                                    label='Name'
                                    placeholder='Enter subcategory name'
                                    value={form['name'] || ''}
                                    onChange={handleChange}
                                    required
                                />

                                <Form.Input
                                    name='pricePerKg'
                                    label='Price Per Kg'
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder='Enter price per kilogram'
                                    value={form['pricePerKg'] || ''}
                                    onChange={handleChange}
                                    required
                                />

                                <Form.Input
                                    name='minTravelerReward'
                                    label='Traveler Reward'
                                    placeholder='Enter minimum traveler reward'
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={form['minTravelerReward'] || ''}
                                    onChange={handleChange}
                                    required
                                />

                                <Form.Input
                                    name='weight'
                                    label='Weight'
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder='Enter weight'
                                    value={form['weight'] || ''}
                                    onChange={handleChange}
                                    required
                                />

                                <Form.Group>
                                    <Form.Label>Icon</Form.Label>
                                    <input 
                                        type='file' 
                                        ref={iconFile}
                                        accept="image/*"
                                        onChange={handleIconChange}
                                        style={{ display: 'none' }}
                                    />
                                    
                                    {!getCurrentIconSrc() ? (
                                        <div className="drop-zone" onClick={handleIconDropZoneClick}>
                                            <div className="drop-zone-icon">
                                                <svg 
                                                    width="48" 
                                                    height="48" 
                                                    viewBox="0 0 24 24" 
                                                    fill="none" 
                                                    stroke="#6c757d" 
                                                    strokeWidth="1.5" 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round"
                                                >
                                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                                    <circle cx="8.5" cy="8.5" r="1.5"/>
                                                    <polyline points="21,15 16,10 5,21"/>
                                                </svg>
                                            </div>
                                            <div className="drop-zone-text">
                                                {removeIcon ? 'Click here to upload new icon' : 'Click here to upload subcategory icon'}
                                            </div>
                                            <div className="drop-zone-hint">JPG, PNG, GIF supported</div>
                                        </div>
                                    ) : (
                                        <div className="image-preview-container">
                                            <img 
                                                src={getCurrentIconSrc()} 
                                                alt="Icon Preview" 
                                                className="image-preview"
                                                onClick={handleIconDropZoneClick}
                                            />
                                            <button
                                                type="button"
                                                className="remove-image-btn"
                                                onClick={handleRemoveIcon}
                                            >
                                                ×
                                            </button>
                                            {!hasNewIcon && form?.icon && !removeIcon && (
                                                <div className="image-note">Current icon shown. Click to change.</div>
                                            )}
                                            {hasNewIcon && (
                                                <div className="image-note">New icon selected. Click to change.</div>
                                            )}
                                        </div>
                                    )}
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label>Photo</Form.Label>
                                    <input 
                                        type='file' 
                                        ref={photoFile}
                                        accept="image/*"
                                        onChange={handlePhotoChange}
                                        style={{ display: 'none' }}
                                    />
                                    
                                    {!getCurrentPhotoSrc() ? (
                                        <div className="drop-zone" onClick={handlePhotoDropZoneClick}>
                                            <div className="drop-zone-icon">
                                                <svg 
                                                    width="48" 
                                                    height="48" 
                                                    viewBox="0 0 24 24" 
                                                    fill="none" 
                                                    stroke="#6c757d" 
                                                    strokeWidth="1.5" 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round"
                                                >
                                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                                    <circle cx="8.5" cy="8.5" r="1.5"/>
                                                    <polyline points="21,15 16,10 5,21"/>
                                                </svg>
                                            </div>
                                            <div className="drop-zone-text">
                                                {removePhoto ? 'Click here to upload new photo' : 'Click here to upload subcategory photo'}
                                            </div>
                                            <div className="drop-zone-hint">JPG, PNG, GIF supported</div>
                                        </div>
                                    ) : (
                                        <div className="image-preview-container">
                                            <img 
                                                src={getCurrentPhotoSrc()} 
                                                alt="Photo Preview" 
                                                className="image-preview"
                                                onClick={handlePhotoDropZoneClick}
                                            />
                                            <button
                                                type="button"
                                                className="remove-image-btn"
                                                onClick={handleRemovePhoto}
                                            >
                                                ×
                                            </button>
                                            {!hasNewPhoto && form?.image && !removePhoto && (
                                                <div className="image-note">Current photo shown. Click to change.</div>
                                            )}
                                            {hasNewPhoto && (
                                                <div className="image-note">New photo selected. Click to change.</div>
                                            )}
                                        </div>
                                    )}
                                </Form.Group>
                            </Form>
                        </Card.Body>
                        <Card.Footer>
                            <div className="modal-footer">
                                <Button
                                    color="primary"
                                    size="sm"
                                    className={`ms-2 ${saveLoading ? 'btn-loading' : ''}`}
                                    onClick={saveEdit}
                                    disabled={saveLoading}
                                >
                                    Save Changes
                                </Button>
                                <Button
                                    color="secondary"
                                    size="sm"
                                    className="ms-2"
                                    onClick={closeMainModal}
                                    disabled={saveLoading}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </Card.Footer>
                    </>
                )}
            </Dimmer>
        </Card>
    );
};

export default SubcategoryEditModal;